import moment from 'moment';
export const APP_STATUS = {
    SIGNUP: 'SIGNUP',
    QUALIFICATION: 'QUALIFICATION',
    FINALE: 'FINALE',
    CLOSED: 'CLOSED',
};
/**
 * Safe defaults for campaigns without a usable phase configuration.
 *
 * @returns A `ProcessedPhases` object in the SIGNUP state with every date and flag unset.
 */
export const emptyProcessedPhases = () => ({
    appStatus: APP_STATUS.SIGNUP,
    phaseDate: null,
    countdownMessage: '',
    campaignLaunch: null,
    voteStart: null,
    voteStartFinale: null,
    voteEnd: null,
    campaignLaunched: false,
    voteStarted: false,
    voteStartedFinale: false,
    voteEnded: false,
    signUpActive: false,
    hasQualification: false,
    hasFinale: false,
    type: [],
    phases: [],
});
/** Maps the two voting windows onto the legacy `type` array. */
const buildLegacyTypes = (plan) => {
    const typeArray = [];
    if (plan.qualification) {
        typeArray.push({
            type: 'fingerprint', // Legacy name for qualification
            start: plan.qualification.start,
            end: plan.qualification.end,
            phaseType: 'fingerprint',
            phaseStart: plan.qualification.start,
            phaseEnd: plan.qualification.end,
            phaseOrder: 0,
        });
    }
    if (plan.finale) {
        typeArray.push({
            type: 'sms', // Legacy name for finale
            start: plan.finale.start,
            end: plan.finale.end,
            phaseType: 'sms',
            phaseStart: plan.finale.start,
            phaseEnd: plan.finale.end,
            phaseOrder: plan.qualification ? 1 : 0,
        });
    }
    return typeArray;
};
/**
 * Derives the campaign status, the countdown target and the phase flags from a phase plan.
 *
 * The single difference between the two products is structural, not a branch: without a
 * qualification window the vote start equals the finale start, so `voteStarted` and
 * `voteStartedFinale` always agree, `QUALIFICATION` is unreachable and the countdown never shows
 * "bis zum Finale". Nothing here knows which product it runs for.
 *
 * @param plan   Phase structure of the campaign.
 * @param now    Reference point in time, defaults to the current time.
 * @returns The derived `ProcessedPhases`.
 */
export const derivePhaseStatus = (plan, now = moment()) => {
    const reference = moment(now);
    const voteStart = plan.qualification ? plan.qualification.start : plan.finale ? plan.finale.start : null;
    const voteStartFinale = plan.finale ? plan.finale.start : null;
    const voteEnd = plan.finale ? plan.finale.end : plan.qualification ? plan.qualification.end : null;
    const campaignLaunched = plan.campaignLaunch ? reference.isSameOrAfter(moment(plan.campaignLaunch)) : false;
    const voteStarted = voteStart ? reference.isSameOrAfter(moment(voteStart)) : false;
    const voteStartedFinale = voteStartFinale ? reference.isSameOrAfter(moment(voteStartFinale)) : false;
    const voteEnded = voteEnd ? reference.isSameOrAfter(moment(voteEnd)) : false;
    // With a signup stage the signup is active until its own end; without one it stays active until
    // the vote closes (Cassiopeia: signup starts at launch and ends when voting ends).
    const signUpActive = plan.signupEnd
        ? campaignLaunched && reference.isBefore(moment(plan.signupEnd))
        : campaignLaunched && !voteEnded;
    // Determine current App Status
    let appStatus = APP_STATUS.SIGNUP;
    if (voteEnded) {
        appStatus = APP_STATUS.CLOSED;
    }
    else if (voteStartedFinale) {
        appStatus = APP_STATUS.FINALE;
    }
    else if (voteStarted) {
        appStatus = APP_STATUS.QUALIFICATION;
    }
    else if (!signUpActive && campaignLaunched) {
        // Signup finished but voting not yet started
        appStatus = APP_STATUS.CLOSED;
    }
    // Helper for Countdown.js
    let phaseDate = '';
    let countdownMessage = '';
    if (!voteStarted) {
        phaseDate = voteStart;
        countdownMessage = 'bis zur Abstimmung';
    }
    else if (!voteEnded) {
        if (plan.qualification && plan.finale) {
            if (voteStartedFinale) {
                phaseDate = voteEnd;
                countdownMessage = 'bis zum Ende';
            }
            else {
                phaseDate = voteStartFinale;
                countdownMessage = 'bis zum Finale';
            }
        }
        else {
            phaseDate = voteEnd;
            countdownMessage = 'bis zum Ende';
        }
    }
    else {
        countdownMessage = 'ist beendet';
        phaseDate = null;
    }
    return {
        appStatus,
        phaseDate,
        countdownMessage,
        campaignLaunch: plan.campaignLaunch,
        voteStart,
        voteStartFinale,
        voteEnd,
        campaignLaunched,
        voteStarted,
        voteStartedFinale,
        voteEnded,
        signUpActive,
        hasQualification: !!plan.qualification,
        hasFinale: !!plan.finale,
        type: buildLegacyTypes(plan),
        phases: [],
    };
};
/**
 * Array-shape adapter: derives the plan from the `phases: [...]` list of the Bigfoot-family
 * backends and the admin cockpits.
 *
 * @param phases Configured stages.
 * @param now    Reference point in time, defaults to the current time.
 * @returns `emptyProcessedPhases()` when no stage is configured, otherwise the derived status.
 */
export const transformPhases = (phases, now) => {
    if (!phases || !Array.isArray(phases) || phases.length === 0) {
        return emptyProcessedPhases();
    }
    const signupPhase = phases.find((p) => p.type === 'signup');
    const qualificationPhase = phases.find((p) => p.type === 'qualification');
    const finalePhase = phases.find((p) => p.type === 'finale');
    const plan = {
        campaignLaunch: signupPhase ? signupPhase.start : null,
        signupEnd: signupPhase ? signupPhase.end : null,
        qualification: qualificationPhase ? { start: qualificationPhase.start, end: qualificationPhase.end } : null,
        finale: finalePhase ? { start: finalePhase.start, end: finalePhase.end } : null,
    };
    // The admin cockpits edit the raw stage list, so it is handed through unchanged.
    return { ...derivePhaseStatus(plan, now), phases };
};
/**
 * Object-shape adapter: derives the plan from the single-vote-window configuration of the
 * Cassiopeia backend.
 *
 * Cassiopeia has no qualification stage — the vote window is the finale window. That is expressed
 * by the shape of the plan (`qualification: null`), not by a product switch, so the shared core
 * reproduces the historic Cassiopeia status without special cases.
 *
 * @param phases Configured windows.
 * @param now    Reference point in time, defaults to the current time.
 * @returns `emptyProcessedPhases()` when a window is missing, otherwise the derived status.
 */
export const transformLegacyPhases = (phases, now) => {
    if (!phases || !phases.campaignLaunch || !phases.voteStart || !phases.voteEnd) {
        return emptyProcessedPhases();
    }
    const plan = {
        campaignLaunch: phases.campaignLaunch,
        signupEnd: null,
        qualification: null,
        finale: { start: phases.voteStart, end: phases.voteEnd },
    };
    return derivePhaseStatus(plan, now);
};
/**
 * `true` when `now` has reached `date`. The target is truncated to whole seconds first, matching the
 * historic server-side comparison of the formatted timestamps.
 */
const hasReached = (reference, date) => date ? reference.isSameOrAfter(moment(date).format('YYYY-MM-DDTHH:mm:ss')) : false;
/**
 * Server-side phase derivation for the Cassiopeia client configuration.
 *
 * @param windows Configured windows.
 * @param now     Reference point in time, defaults to the current time.
 * @returns The three booleans the `/config` and `/config/phases` endpoints expose.
 */
export const deriveVotingFlags = (windows, now = moment()) => {
    const reference = moment(now);
    return {
        campaignLaunched: hasReached(reference, windows?.campaignLaunch),
        votingStarted: hasReached(reference, windows?.voteStart),
        votingEnded: hasReached(reference, windows?.voteEnd),
    };
};
//# sourceMappingURL=phases.js.map